import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { listPendingTeamInvites, acceptTeamInvite, declineTeamInvite } from '@/lib/pro-team-api';

export default function TeamInvitesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth();

  const deepLinkInviteId = searchParams.get('invite_id') || undefined;
  const deepLinkToken = searchParams.get('token') || undefined;

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const res = await listPendingTeamInvites(); setInvites(res.invites || []); }
    catch (e) { setError(e?.message || 'Could not load team invites.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onAccept(inv) {
    setBusy(inv.id);
    try {
      const tk = deepLinkInviteId === inv.id ? deepLinkToken : undefined;
      await acceptTeamInvite(inv.id, tk);
      setToast({ type: 'success', text: `You\u2019ve joined ${inv.owner_name || 'the team'}. Ask them to grant you co-host access to events you\u2019ll help run.` });
      await load();
    } catch (e) {
      setToast({ type: 'error', text: e?.message || 'Could not accept invite.' });
    } finally { setBusy(null); }
  }

  async function onDecline(inv) {
    setBusy(inv.id);
    try {
      const tk = deepLinkInviteId === inv.id ? deepLinkToken : undefined;
      await declineTeamInvite(inv.id, tk);
      await load();
    } catch (e) {
      setToast({ type: 'error', text: e?.message || 'Could not decline invite.' });
    } finally { setBusy(null); }
  }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="team-invites-screen">
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-semibold max-w-sm text-center ${toast.type === 'success' ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#FEF2F2] text-[#DC2626]'}`} data-testid="team-invites-toast">
          {toast.text}
        </div>
      )}

      <div className="flex items-center justify-between mb-1">
        <button onClick={() => navigate(-1)} className="p-1 -ml-1 hover:bg-[#F3F5F8] rounded-lg transition-colors" data-testid="team-invites-back-btn"><ChevronLeft className="w-5 h-5 text-[#0B1B46]" /></button>
        <h1 className="text-xl font-extrabold text-[#0B1B46]" data-testid="team-invites-title">Team invites</h1>
        <div className="w-5" />
      </div>
      <p className="text-sm text-[#5B6472] mb-5">Professional hosts can invite you to help run their events as a co-host. You can accept or decline each invite.</p>

      {loading ? (
        <div className="flex justify-center py-10" data-testid="team-invites-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="team-invites-error">
          <p className="text-sm font-semibold text-[#DC2626]">{error}</p>
          <button onClick={load} className="mt-2 h-9 px-4 bg-[#146BFF] text-white font-semibold rounded-lg text-sm" data-testid="team-invites-retry-btn">Retry</button>
        </div>
      ) : invites.length === 0 ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5" data-testid="team-invites-empty">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">No team invites</p>
          <p className="text-sm text-[#5B6472]">Any incoming team invites will show up here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {invites.map((inv) => (
            <div key={inv.id} className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid={`team-invites-card-${inv.id}`}>
              <p className="text-base font-extrabold text-[#0B1B46]">{inv.owner_name || 'A Professional host'}</p>
              <p className="text-[11px] text-[#5B6472] mt-1">Sent {new Date(inv.created_at).toLocaleDateString()} &middot; Expires {new Date(inv.expires_at).toLocaleDateString()}</p>
              <div className="flex gap-3 mt-3">
                <button onClick={() => onAccept(inv)} disabled={busy !== null}
                  className="flex-1 h-10 bg-[#146BFF] hover:bg-[#0F55CC] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid={`team-invites-accept-${inv.id}`}>
                  {busy === inv.id && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  Accept
                </button>
                <button onClick={() => onDecline(inv)} disabled={busy !== null}
                  className="flex-1 h-10 border border-[#E3E8EF] text-sm font-semibold text-[#0B1B46] rounded-lg hover:bg-[#F3F5F8] transition-colors disabled:opacity-60"
                  data-testid={`team-invites-decline-${inv.id}`}>
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
